/**
 * E06-F05-T08 — human wording for a FAILED per-company run (site scrape + Haiku chain).
 *
 * The problem this module removes: the scrape/AI stack stores its raw technical
 * message on the row (`research_companies.ai_run_error`, e.g. `GET http://acme.com/ →
 * HTTP 403`) and the AI-check screen printed that string verbatim. A recruiter reading
 * "HTTP 403" learns nothing and has no idea what to do next.
 *
 * The fix is a PURE, DOM-free classifier shared by api (which projects the row onto the
 * wire DTO) and web (which renders it): the raw message is mapped onto a small
 * vocabulary of causes, each with a natural-language sentence. The raw text is NEVER
 * thrown away — it travels next to the message as `detail` and the UI keeps it behind a
 * "Technical details" disclosure, so support/debugging loses nothing.
 *
 * Why classify a STRING and not an error object: the message is persisted in a plain
 * text column, so every historical row (written before this change, and by any future
 * producer) must classify too. The producers stay untouched — one reader-side mapping,
 * no migration, no new column.
 *
 * IDEMPOTENT by construction: feeding an already-humanised message back in returns that
 * same message with `detail: null` (see the reverse lookup in `describeRunFailure`), so
 * a rolling deploy (old API emitting raw text ↔ new bundle, or vice versa) can never
 * produce "The AI check could not be completed" on top of an already good sentence.
 */
import { z } from 'zod';
/**
 * The failure vocabulary. Consumers branch on the CODE, never on the wording
 * (same rule as `COMPANY_FACTS_FAILURE_CODES` in contracts/kickoff-enrichment.ts).
 */
export const RUN_FAILURE_CODES = [
    /** 401/403/405/406/451/… — the site actively refuses automated readers. */
    'SITE_BLOCKED',
    /** 429 — the site throttles us. */
    'SITE_RATE_LIMITED',
    /** 404/410 — the address we hold does not exist there (any more). */
    'SITE_NOT_FOUND',
    /** 5xx — the site itself is broken right now. */
    'SITE_SERVER_ERROR',
    /** DNS / connection / TLS failure — nothing answered. */
    'SITE_UNREACHABLE',
    /** Timeout / aborted request. */
    'SITE_TIMEOUT',
    /** robots.txt disallows the path for our agent. */
    'SITE_DISALLOWED_BY_ROBOTS',
    /**
     * E07-F01-T11 — the site opened but carries no readable text (script-rendered page,
     * parked stub). The AI chain stops BEFORE the paid extract/verify calls.
     */
    'SITE_NO_TEXT',
    /** SSRF guard refused the URL (private/loopback/metadata address). */
    'ADDRESS_NOT_ALLOWED',
    /** No usable domain on the record — nothing to read at all. */
    'NO_COMPANY_WEBSITE',
    /** Anything else (model/provider/parse failure) — generic, never raw. */
    'CHECK_FAILED',
];
export const runFailureCodeSchema = z.enum(RUN_FAILURE_CODES);
/**
 * The sentence shown to the user. One line, plain English, always says what happened
 * AND what the user can do — the "HTTP 403" the code came from stays in `detail`.
 */
export const RUN_FAILURE_MESSAGES = {
    SITE_BLOCKED: 'The company website blocked automated access — open it manually to check.',
    SITE_RATE_LIMITED: 'The company website is limiting automated requests — try again in a few minutes.',
    SITE_NOT_FOUND: 'That page was not found on the company website — check the address on the record.',
    SITE_SERVER_ERROR: 'The company website is having trouble right now — try again later.',
    SITE_UNREACHABLE: 'The company website could not be reached — check the address or try again later.',
    SITE_TIMEOUT: 'The company website took too long to respond — try again later.',
    SITE_DISALLOWED_BY_ROBOTS: 'The company website asks automated tools not to read it, so nothing was collected.',
    SITE_NO_TEXT: 'The company website opened but has no readable text — open it manually to check.',
    ADDRESS_NOT_ALLOWED: 'That web address is not allowed for security reasons — check the website on the record.',
    NO_COMPANY_WEBSITE: 'Website not found — enter the company website in Review and run the check again.',
    CHECK_FAILED: 'The AI check could not be completed — try again in a moment.',
};
/** Cap on the kept technical text (the column allows 1000; the UI shows a snippet). */
const MAX_DETAIL_CHARS = 600;
/** Reverse lookup message → code, so re-classifying our own output is a no-op. */
const MESSAGE_TO_CODE = new Map(Object.entries(RUN_FAILURE_MESSAGES).map(([code, msg]) => [
    msg,
    code,
]));
/** Provider/model failures — checked FIRST so an Anthropic "HTTP 429" is not blamed on the site. */
const AI_MARKERS = /\b(anthropic|openai|claude|haiku|sonnet|gpt|llm|model|provider|prompt|credit balance|api key|token limit|overloaded_error|no llm)\b/i;
/** Explicit "we never had a website" errors (worker + gatherSiteText wording). */
const NO_DOMAIN_MARKERS = /(no website domain|unusable domain|no company website|without a domain)/i;
const ROBOTS_MARKERS = /robots(\.txt)?/i;
/** E07-F01-T11 — the AI chain's own "opened, but empty" error ({@link SITE_NO_TEXT_ERROR}). */
const NO_TEXT_MARKERS = /no readable text/i;
/**
 * The message the AI chain throws when the homepage opened but carried no text at all.
 * Kept here, next to its classifier marker, so producer and reader cannot drift.
 */
export const SITE_NO_TEXT_ERROR = 'website opened but has no readable text — nothing to check';
/** The SSRF guard's own message (`SsrfError`: "Blocked request to <url>: <reason>"). */
const SSRF_MARKERS = /(blocked request to|ssrf|private address|loopback|link-local|metadata)/i;
const TIMEOUT_MARKERS = /(timed out|timeout|ETIMEDOUT|UND_ERR_(?:HEADERS|BODY)_TIMEOUT|abort(?:ed)?|AbortError|deadline)/i;
const UNREACHABLE_MARKERS = /(fetch failed|ENOTFOUND|EAI_AGAIN|ECONNREFUSED|ECONNRESET|EHOSTUNREACH|ENETUNREACH|EPROTO|getaddrinfo|certificate|self.signed|SSL|TLS|socket hang up|terminated|could not be fetched|unreachable|too many redirects)/i;
/** HTTP status → cause. Anything else 4xx is treated as a refusal by the site. */
function codeForStatus(status) {
    if (status === 429)
        return 'SITE_RATE_LIMITED';
    if (status === 404 || status === 410)
        return 'SITE_NOT_FOUND';
    if (status >= 500)
        return 'SITE_SERVER_ERROR';
    if (status >= 400)
        return 'SITE_BLOCKED';
    return 'SITE_UNREACHABLE';
}
/**
 * Map a raw technical failure message onto the {@link RUN_FAILURE_CODES} vocabulary.
 *
 * Order matters: model/provider markers win over an HTTP status (a provider 429 is not
 * the company's website), the SSRF guard and robots win over the generic network
 * patterns, and the fallback is always the neutral `CHECK_FAILED` — a raw string is
 * never promoted to user-facing wording.
 */
export function classifyRunFailure(raw) {
    const text = raw.trim();
    if (!text)
        return 'CHECK_FAILED';
    if (SSRF_MARKERS.test(text))
        return 'ADDRESS_NOT_ALLOWED';
    if (NO_DOMAIN_MARKERS.test(text))
        return 'NO_COMPANY_WEBSITE';
    if (AI_MARKERS.test(text))
        return 'CHECK_FAILED';
    const status = /\bHTTP[\s/]*(\d{3})\b/i.exec(text) ?? /\bstatus(?:\s+code)?[:\s]+(\d{3})\b/i.exec(text);
    if (status)
        return codeForStatus(Number(status[1]));
    if (ROBOTS_MARKERS.test(text))
        return 'SITE_DISALLOWED_BY_ROBOTS';
    if (NO_TEXT_MARKERS.test(text))
        return 'SITE_NO_TEXT';
    if (TIMEOUT_MARKERS.test(text))
        return 'SITE_TIMEOUT';
    if (UNREACHABLE_MARKERS.test(text))
        return 'SITE_UNREACHABLE';
    return 'CHECK_FAILED';
}
/**
 * Split a stored failure into the sentence the user reads and the technical detail
 * kept behind a disclosure. Returns null for "no failure recorded" (null/blank input),
 * so callers can render nothing without a second emptiness check.
 */
export function describeRunFailure(raw) {
    const text = (raw ?? '').trim();
    if (!text)
        return null;
    // Already humanised (our own output came back) → keep it, add no fake detail.
    const known = MESSAGE_TO_CODE.get(text);
    if (known)
        return { code: known, message: RUN_FAILURE_MESSAGES[known], detail: null };
    const code = classifyRunFailure(text);
    const detail = text.length > MAX_DETAIL_CHARS ? `${text.slice(0, MAX_DETAIL_CHARS)}…` : text;
    return { code, message: RUN_FAILURE_MESSAGES[code], detail };
}
/** `describeRunFailure` for a thrown value (producer side: catch → classify). */
export function describeRunFailureFromError(err) {
    if (err == null)
        return null;
    return describeRunFailure(err instanceof Error ? err.message : String(err));
}
//# sourceMappingURL=runFailure.js.map